// Tiny animated before/after preview for the IdeaFusion spotlight.
// Crossfades through 3 Reddit-to-brief pairs on a 5s loop. CSS-only motion.
import { useEffect, useState } from "react";
import { ArrowRight, Sparkles } from "lucide-react";

const PAIRS = [
  {
    reddit: [
      {
        sub: "r/smallbusiness",
        text: "Chasing invoice PDFs across email, Slack, and Drive. Half get paid late.",
      },
      {
        sub: "r/freelance",
        text: "Clients ghost after the project. I need auto follow-ups that don't sound pushy.",
      },
    ],
    brief: {
      concept: "Invoice hunter that auto-chases unpaid PDFs across every inbox.",
      user: "Solo freelancers billing 5–30 invoices a month.",
      value: "Get paid ~12 days faster without writing a single follow-up.",
    },
  },
  {
    reddit: [
      {
        sub: "r/parenting",
        text: "Toddler tantrums in stores. Every tip online is 500 words of fluff.",
      },
      {
        sub: "r/beyondthebump",
        text: "I just want one clear sentence to say in the moment. Not a lecture.",
      },
    ],
    brief: {
      concept: "Pocket coach that hands parents one calm line per meltdown.",
      user: "First-time parents of kids 1–4, short on sleep and screen time.",
      value: "Defuse tantrums in under 30 seconds with expert-crafted scripts.",
    },
  },
  {
    reddit: [
      {
        sub: "r/GetMotivated",
        text: "Journaling apps overwhelm me. I open, freeze, close.",
      },
      {
        sub: "r/productivity",
        text: "Just want one prompt a day. No streaks, no shaming.",
      },
    ],
    brief: {
      concept: "Single-question journal that emails one prompt a day, that's it.",
      user: "Burnt-out professionals who quit every habit app within a week.",
      value: "Build a real reflection habit with zero app to open.",
    },
  },
];

export default function IdeaFusionMicroPreview() {
  const [i, setI] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setI((prev) => (prev + 1) % PAIRS.length);
        setFading(false);
      }, 350);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const pair = PAIRS[i];

  return (
    <div
      data-testid="ideafusion-micro-preview"
      className="mt-6 border border-[#1F1420] bg-[#080608]/80 p-3 sm:p-4"
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 md:gap-4 items-stretch">
        {/* BEFORE — Reddit pain points */}
        <div
          data-testid="ideafusion-micro-before"
          className={`transition-opacity duration-300 ${
            fading ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-2">
            Raw Reddit signals
          </div>
          <div className="space-y-2">
            {pair.reddit.map((r, idx) => (
              <div
                key={idx}
                className="border border-[#1F1F22] bg-[#0B0B0D] px-3 py-2"
              >
                <div className="text-[9.5px] font-mono uppercase tracking-wider text-orange-300/80">
                  {r.sub}
                </div>
                <div className="mt-1 text-[12px] text-zinc-300 leading-snug">
                  &ldquo;{r.text}&rdquo;
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FUSING ARROW */}
        <div className="flex md:flex-col items-center justify-center gap-1 md:gap-2 py-1 md:py-0">
          <span className="hidden md:block h-6 w-px bg-gradient-to-b from-transparent via-fuchsia-400/40 to-transparent" />
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-full border border-fuchsia-400/40 bg-fuchsia-400/[0.08]">
            <span className="absolute inset-0 rounded-full border border-fuchsia-400/30 animate-ping" />
            <Sparkles size={14} className="text-fuchsia-300" />
          </span>
          <ArrowRight
            size={16}
            className="text-fuchsia-300 md:hidden"
          />
          <ArrowRight
            size={16}
            className="text-fuchsia-300 hidden md:block rotate-90"
          />
          <span className="hidden md:block h-6 w-px bg-gradient-to-b from-transparent via-fuchsia-400/40 to-transparent" />
        </div>

        {/* AFTER — Fundable brief */}
        <div
          data-testid="ideafusion-micro-after"
          className={`transition-opacity duration-300 ${
            fading ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-fuchsia-300 mb-2">
            Fundable product brief
          </div>
          <div className="border border-fuchsia-400/25 bg-[#100A14] p-3 space-y-2.5">
            <MicroField label="Concept" value={pair.brief.concept} />
            <MicroField label="Target user" value={pair.brief.user} />
            <MicroField label="Value prop" value={pair.brief.value} />
          </div>
        </div>
      </div>

      {/* Progress dots */}
      <div
        data-testid="ideafusion-micro-dots"
        className="mt-3 flex items-center justify-center gap-1.5"
      >
        {PAIRS.map((_, idx) => (
          <span
            key={idx}
            className={`h-1 rounded-full transition-all duration-300 ${
              idx === i ? "w-5 bg-fuchsia-300" : "w-1.5 bg-zinc-700"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function MicroField({ label, value }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">
        {label}
      </div>
      <div className="mt-0.5 text-[12px] text-zinc-100 leading-snug">
        {value}
      </div>
    </div>
  );
}
